import { Injectable } from '@angular/core';
import { GalleryTheme, Section } from '@nusign/picflow-backend-types';
import { Rect } from '../../classes/rect.class';
import { ScreenSizes } from '../../classes/screen-sizes.class';
import { assetFilenameSpace } from '../../consts';
import { calcColumnCount } from '../../lib/calc-column-count';
import { GalleryItem } from '../classes/gallery-item.class';
import { getAssetDimentions } from '../lib/get-asset-dimentions';
import { getGalleryWidth } from '../lib/get-gallery-width';

@Injectable({ providedIn: 'root' })
export class LayoutMosaicRowsService {
  calculateAllItemPositions(
    section: Section,
    theme: GalleryTheme,
    items: GalleryItem[],
    pageScale = 1,
    fullWidth = false,
    maxWidth?: number,
    noOuterSpacing = false
  ): number {
    const { galleryWidth, columnCount, columnWidth, spacing } = this.calculateSectionDimentions(
      section,
      theme,
      items,
      pageScale,
      fullWidth,
      maxWidth,
      noOuterSpacing
    );

    const rows: { items: GalleryItem[]; width: number }[] = [];
    let row: { items: GalleryItem[]; width: number } = { items: [], width: 0 };

    for (let i = 0; i < items.length; i++) {
      row.width = this.getRowWidth([items[i], ...row.items], columnWidth);
      if (row.width > galleryWidth + columnWidth / 2) {
        rows.push(row);
        row = { items: [items[i]], width: 0 };
      } else {
        row.items.push(items[i]);
      }
    }
    if (!rows.includes(row) && row.items.length) rows.push(row);

    rows.forEach((row, y) => {
      if (y === rows.length - 1 && row.items.length < columnCount) {
        const toAdd = columnCount - row.items.length;
        for (let i = 0; i < toAdd; i++) {
          if (ScreenSizes.screenSize() !== 'mobile') {
            row.items.push(undefined);
          }
        }
      }
      const upRow = rows[y - 1];
      const posY = upRow?.items[0]?.dimensions.y + upRow?.items[0]?.dimensions.height || 0;
      let totalAspect = 0;
      row.items.forEach(item => {
        if (!item?.asset?.version_data?.data?.width) {
          totalAspect += 1;
        } else {
          const { width, height } = getAssetDimentions(item.asset.version_data);
          totalAspect += width / height;
        }
      });
      row.items.forEach((item, x) => {
        if (!item?.asset) return;
        const leftItem = row.items[x - 1];
        const _spacing =
          y === 0 ? 0 : spacing + ((section.show_filenames ?? theme.section.show_filenames) ? assetFilenameSpace : 0);
        if (leftItem) {
          item.bounds = new Rect(leftItem.bounds.x + leftItem.bounds.width, posY + _spacing);
        } else {
          item.bounds = new Rect(0, posY + _spacing);
        }

        const { width, height } = getAssetDimentions(item.asset.version_data);

        let percent = width ? (100 / totalAspect) * (width / height) : 100 / totalAspect;
        if (percent > 100) percent = 100;
        item.dimensions.width = Math.round(((galleryWidth - spacing * 2 * row.items.length) / 100) * percent);
        const aspect = (width ? height / width : 1) || 1;
        item.dimensions.height = Math.round(item.dimensions.width * aspect);

        item.bounds.width = Math.round(item.dimensions.width + spacing * 2);
        item.bounds.height = Math.round(item.dimensions.height + spacing * 2);

        item.dimensions.x = Math.round(item.bounds.x + (noOuterSpacing ? 0 : spacing));
        item.dimensions.y = Math.round(item.bounds.y + spacing);
      });
    });
    return galleryWidth;
  }

  private getRowWidth(row: GalleryItem[], columnWidth: number): number {
    let totalWidth = 0;
    row.forEach(item => {
      if (!item.asset) return;
      const height = columnWidth;
      const data = item.asset.version_data.data?.width ? item.asset.version_data.data : { width: 1, height: 1 };
      const dataWidth = Number(data.width);
      const dataHeight = Number(data.height);
      const aspect = data ? dataWidth / dataHeight : 1;
      totalWidth += height * aspect;
    });
    return totalWidth;
  }

  public calculateSectionDimentions(
    section: Section,
    theme: GalleryTheme,
    items: GalleryItem[],
    pageScale = 1,
    fullWidth = false,
    maxWidth?: number,
    noOuterSpacing = false
  ) {
    let spacing = section.spacing ?? theme.section.spacing;
    if (ScreenSizes.screenSize() === 'mobile') spacing /= 2;
    let galleryWidth = getGalleryWidth(section, theme, fullWidth, maxWidth);
    if (noOuterSpacing) {
      if (ScreenSizes.screenSize() === 'mobile') galleryWidth += spacing * 2;
      else galleryWidth += spacing * 2;
    }
    let columnCount = calcColumnCount(section, theme);
    let columnWidth = Math.round(galleryWidth / columnCount);

    if (columnCount > items.length) {
      columnCount = items.length;
      galleryWidth = Math.min(galleryWidth, columnWidth * items.length * 2);
    }

    galleryWidth = Math.round(galleryWidth * pageScale);
    columnWidth = Math.round(columnWidth * pageScale);
    spacing = Math.round(spacing * pageScale);

    return { galleryWidth, columnCount, columnWidth, spacing };
  }
}
